import { startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { BuyCredits, PurchaseHistory, PurchaseOutcome } from '../components/billing';
import { RecoveryBanner } from '../components/recovery';
import { ApiError, api } from '../lib/api';
import { errorMessage, usd, when } from '../lib/format';
import { useSession } from '../lib/session';

export function AccountPage() {
  const { enrol, purchase, outcome } = useSearch({ from: '/app/account' });
  const session = useSession();
  const qc = useQueryClient();
  const credits = useQuery({ queryKey: ['credits'], queryFn: api.credits });
  const ledger = useQuery({ queryKey: ['ledger'], queryFn: () => api.ledger(50) });
  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.usage(24) });
  const workspace = useQuery({
    queryKey: ['workspace'],
    queryFn: api.workspace,
    staleTime: 60_000,
  });
  const audit = useQuery({ queryKey: ['audit'], queryFn: () => api.audit(50) });
  const [pkName, setPkName] = useState('');
  const [pkError, setPkError] = useState<string | null>(null);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['session'] });
    qc.invalidateQueries({ queryKey: ['credits'] });
    qc.invalidateQueries({ queryKey: ['ledger'] });
  };

  const addPasskey = useMutation({
    mutationFn: async () => {
      setPkError(null);
      const { challengeId, options } = await api.passkeyRegisterOptions();
      const response = await startRegistration({
        optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await api.passkeyRegisterVerify(challengeId, response, pkName.trim() || undefined);
      setPkName('');
    },
    onSuccess: refreshAll,
    onError: (e) => setPkError(errorMessage(e)),
  });
  const removePasskey = useMutation({
    mutationFn: (id: string) => api.removePasskey(id),
    onSuccess: refreshAll,
  });
  const topup = useMutation({
    mutationFn: () => api.devTopup(2_000_000),
    onSuccess: refreshAll,
  });
  const topupDisabled = topup.error instanceof ApiError && topup.error.status === 403;
  const onPurchaseSettled = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['credits'] });
    qc.invalidateQueries({ queryKey: ['purchases'] });
  }, [qc]);
  const cancelRecovery = useMutation({
    mutationFn: (id: string) => api.cancelRecovery(id),
    onSuccess: refreshAll,
  });

  const passkeys = session.data?.passkeys ?? [];
  return (
    <div className="page stack">
      <h1>Account</h1>
      {session.data?.recovery && (
        <RecoveryBanner
          recovery={session.data.recovery}
          canCancel={session.data.method === 'passkey' || session.data.method === 'dev'}
          onCancel={() => cancelRecovery.mutate(session.data?.recovery?.id ?? '')}
          busy={cancelRecovery.isPending}
        />
      )}
      {cancelRecovery.error && <p className="error">{errorMessage(cancelRecovery.error)}</p>}

      <section className="panel">
        <div className="row">
          <div className="grow">
            <div>{session.data?.email}</div>
            <div className="muted">
              signed in via {session.data?.method}; session renews on use, expires{' '}
              {session.data ? when(session.data.expiresAt) : ''}
            </div>
          </div>
        </div>
      </section>

      <section className="panel stack">
        <h2 style={{ marginTop: 0 }}>Passkeys</h2>
        {enrol && passkeys.length === 0 && (
          <p className="warn">
            Add a passkey now so you can sign in without waiting for an email next time. It is bound
            to this site and never leaves your device.
          </p>
        )}
        {passkeys.length === 0 ? (
          <p className="muted">No passkeys yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Added</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {passkeys.map((p) => (
                <tr key={p.id}>
                  <td>{p.name ?? <span className="muted">unnamed</span>}</td>
                  <td>
                    {p.deviceType === 'multiDevice' ? 'synced' : 'device-bound'}
                    {p.backedUp ? ', backed up' : ''}
                  </td>
                  <td>{when(p.createdAt)}</td>
                  <td>{p.lastUsedAt ? when(p.lastUsedAt) : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="small danger"
                      disabled={removePasskey.isPending}
                      onClick={() => {
                        if (confirm(`Remove passkey "${p.name ?? p.id.slice(0, 8)}"?`))
                          removePasskey.mutate(p.id);
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {passkeys.length === 1 && (
          <p className="muted">
            One passkey is a single point of failure. Add a second one on another device, or keep
            email sign-in as the fallback.
          </p>
        )}
        <div className="row">
          <input
            placeholder="Name this passkey (e.g. MacBook, YubiKey)"
            value={pkName}
            onChange={(e) => setPkName(e.target.value)}
          />
          <button
            type="button"
            className="primary"
            disabled={addPasskey.isPending}
            onClick={() => addPasskey.mutate()}
          >
            Add passkey
          </button>
        </div>
        {pkError && <p className="error">{pkError}</p>}
      </section>

      <section className="panel stack">
        <h2 style={{ marginTop: 0 }}>Credits</h2>
        {credits.data && (
          <div className="row wrap">
            <div className="grow">
              <div style={{ fontSize: 22 }}>{usd(credits.data.available, 2)}</div>
              <div className="muted">
                available · {usd(credits.data.openHolds, 4)} held for in-flight turns ·{' '}
                {usd(credits.data.debits, 4)} spent of {usd(credits.data.credits, 2)} bought
              </div>
            </div>
            {!topupDisabled && (
              <button
                type="button"
                disabled={topup.isPending}
                onClick={() => topup.mutate()}
                title="dev only: adds $2.00"
              >
                Dev top-up $2
              </button>
            )}
          </div>
        )}
        {purchase && (
          <PurchaseOutcome purchaseId={purchase} outcome={outcome} onSettled={onPurchaseSettled} />
        )}
        <h3 style={{ margin: 0 }}>Buy credits</h3>
        <BuyCredits />
        <PurchaseHistory />
        {workspace.data?.retention.deleteAt && (
          <p className="muted" data-testid="retention-note">
            Workspace VM: idle VMs are deleted after{' '}
            {Math.round(workspace.data.retention.idleDeleteMs / 86_400_000)} days. Yours was last
            used{' '}
            {workspace.data.retention.lastActivityAt
              ? when(workspace.data.retention.lastActivityAt)
              : 'never'}{' '}
            and is deleted {when(workspace.data.retention.deleteAt)} unless you run something before
            then. Files on the VM go with it; chats, dashboard, credits and connectors stay.
          </p>
        )}
        {usage.data?.workspaceId && (
          <p className="muted">
            Workspace last 24 h: {usage.data.awakeSec}s awake, {usage.data.cpuSec}s CPU,{' '}
            {usage.data.gbHours} GB·h → charged{' '}
            {usd(usage.data.chargedMicroUsd.vm_cpu + usage.data.chargedMicroUsd.vm_memory, 4)}
          </p>
        )}
        {ledger.data && (
          <details>
            <summary className="muted">Ledger (last 50)</summary>
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th>Meter</th>
                  <th>Amount</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                {ledger.data.entries.map((e) => (
                  <tr key={e.id}>
                    <td>{when(e.at)}</td>
                    <td>{e.kind}</td>
                    <td>{e.meter}</td>
                    <td>{usd(e.amount, 6)}</td>
                    <td className="muted">
                      <code>
                        {Object.entries(e.ref)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(' ')}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </section>

      <section className="panel stack">
        <h2 style={{ marginTop: 0 }}>Connector audit log</h2>
        <p className="muted">
          Every connector call, by you or by the agent, with the authorization it carried (D6).
        </p>
        {audit.data && audit.data.entries.length === 0 && <p className="muted">Nothing yet.</p>}
        {audit.data && audit.data.entries.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Call</th>
                <th>Authorization</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.entries.map((e) => (
                <tr key={e.id}>
                  <td>{when(e.at)}</td>
                  <td>
                    <code>
                      {e.connector ? `${e.connector}.` : ''}
                      {e.tool}
                    </code>
                  </td>
                  <td>{e.authorization ?? <span className="muted">none (agent)</span>}</td>
                  <td className={e.outcome === 'denied' ? 'warn' : ''}>{e.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
